import {Module} from 'vuex';
import * as firebase from 'firebase/app';
import 'firebase/firestore';
import moment from 'moment';
import {FamilyHubUser} from '@/models/FamilyHubUser';
import {serializer} from '../../api/shared/serializer';
import {ResidentStatus} from '@/enums/ResidentStatus';
import {FamilyHubUserStatus, FamilyHubUserStatusReason} from '@/enums/FamilyHubUserStatus';
import {MemberInviteStatuses} from '@/enums/MemberInviteStatuses';
import {FamilyHubAppStatus} from '@/enums/FamilyHubAppStatus';
import {getRegionName} from '@/utils/functionsRegion';
import {ResidentParams} from "@/consts/ResidentParams";
import {UserTypeEnum} from '@/enums/UserTypeEnum';
import {DEFAULT_AVATAR_WHITE, DEFAULT_AVATAR_WHITE_200x200} from '@/consts/default-avatar';
import {EventsTypes} from '@/enums/EventsTypes';
import {ResidentsFilters} from "@/models/resident/ResidentsFilters";
import { getFormattedDateTimeString } from '@/utils/dateTimeFormat';
import {EventsTypesMinor} from "@/enums/EventsTypesMinor";
import {getWhereIdInIdsExtended} from "@/utils/getWhereIdInIds";
import * as Sentry from "@sentry/vue";

const residentModule: Module<any, any> = {
    namespaced: false,
    state: {
        residents: [],
        residentsShortData: [],
        currentResident: null,
        residentFamilyMembers: [],
        familyMembers: [],
        familyMember: null,
        residentLogs: [],
        residentsWithUpcomingBirthdays: [],
        leastEngagedResidents: [],
        onResidentsUnsubscribe: null,
        mainResidentsFilters: new ResidentsFilters(),
        upcomingCallsByResident: [],
    },

    mutations: {
        setResidents(state: any, payload: []) {
            state.residents = payload;
        },

        setResidentsShortData(state: any, payload: []) {
            state.residentsShortData = payload;
        },

        setCurrentResident(state: any, payload: any) {
            state.currentResident = payload;
        },

        setAllFamilyMembers(state: any, payload: []) {
            state.familyMembers = payload;
        },

        setResidentFamilyMembers(state: any, payload: []) {
            state.residentFamilyMembers = payload;
        },

        setCurrentFamilyMember(state: any, payload: []) {
            state.familyMember = payload;
        },

        setCurrencyFamilyMemberVisits(state: any, payload: []) {
            state.familyMember.recentVisits = payload;
        },

        setResidentLogs(state: any, payload: []) {
            state.residentLogs = payload;
        },

        setResidentsWithUpcomingBirthdays(state: any, payload: []) {
            state.residentsWithUpcomingBirthdays = payload;
        },

        setLeastEngagedResidents(state: any, payload: []) {
            state.leastEngagedResidents = payload;
        },

        setFamilyMemberInvitations(state: any, payload: []) {
            state.familyMemberInvitations = payload;
        },

        addResidentLogs(state: any, payload: []) {
            state.residentLogs = [...state.residentLogs, ...payload];
        },
        setMainResidentsFilters(state: any, payload: any) {
            const filters = state.mainResidentsFilters;

            if (payload?.hasOwnProperty('wingName')) {
                filters.wingName = payload.wingName;
            }

            state.mainResidentsFilters = filters;
        },
        setUpcomingCallsByResident(state: any, payload: []) {
            state.upcomingCallsByResident = payload;
        },
    },

    actions: {

        async getResidentsShortData({commit, dispatch}, status) {

            let devices = this.state.devicesModule.devices

            if (!devices?.length) {
                devices = await dispatch("getDevices");
            }

            return await firebase
                .app()
                .functions(getRegionName())
                .httpsCallable("getResidentsShortData")({
                    facilityId: this.state.authModule.currentFacility,
                    status: status,
                    params: ResidentParams
                })
                .then(async (data: any) => {
                    const residents = data.data.data.residents as [];

                    commit("setResidentsShortData", residents)

                    return residents
                })
        },

        getFamilyMembers({commit}) {
            firebase
                .firestore()
                .collection('familyHubUsers')
                .where('facilityId', '==', this.state.authModule.currentFacility)
                .get()
                .then(querySnapshot => {
                    const facility = this.state.facilityModule.selectedFacility
                    const familyMembers = querySnapshot.docs.map(familyMemberSnap => {
                    const response = familyMemberSnap.data();
                    const lastActive = response?.lastActive?.seconds? new Date(response?.lastActive.seconds * 1000): null
                    response.id = familyMemberSnap.id;
                    if(lastActive){
                        response.lastActiveShortDisplay = getFormattedDateTimeString(lastActive, facility?.dateFormat, facility?.timeFormat, false) 
                        response.lastActiveFullDisplay = getFormattedDateTimeString(lastActive, facility?.dateFormat, facility?.timeFormat, true) 
                    }
                    else{
                        response.lastActiveShortDisplay = '-'
                        response.lastActiveFullDisplay = '-'
                    }

                    return { ...response }
                });

                commit('setAllFamilyMembers', familyMembers);
            });
        },

        async getFamilyMembersByResident({commit}, {residentId}) {

            const orgId = this.state.facilityModule.selectedFacility.orgId

            await firebase.app().functions(getRegionName())
                .httpsCallable("groupC-getFamilyMembersByResidentId")({
                    orgId: orgId,
                    residentId: residentId
                })
                .then(snap => {
                    console.log()
                    const data = snap?.data?.data || []
                    commit('setResidentFamilyMembers', data);

                }).catch((error) => {
                    console.error(error)
                    commit('setResidentFamilyMembers', []);
                })

        },

        getFamilyMember({commit, dispatch, state}, {id, residentId}) {
            const familyMember = state.familyMembers.find(member => member.id === id);

            if (familyMember) {
                commit('setCurrentFamilyMember', familyMember);

                if (residentId) {
                    dispatch('getResident', residentId);
                }

                dispatch('getFamilyMemberVisits', familyMember);
            } else {
                firebase
                    .firestore()
                    .collection('familyHubUsers')
                    .doc(id)
                    .get()
                    .then(querySnapshot => {
                        const familyMember = querySnapshot.data();
                        const facility = this.state.facilityModule.selectedFacility.selectedFacility
                        
                        if (querySnapshot.exists) {
                            familyMember.id = querySnapshot.id;
  
                            const lastActive = familyMember?.lastActive?.seconds? new Date(familyMember?.lastActive.seconds * 1000): null
                            if(lastActive){
                                familyMember.lastActiveShortDisplay = getFormattedDateTimeString(lastActive, facility.dateFormat, facility.timeFormat, false)
                                familyMember.lastActiveFullDisplay = getFormattedDateTimeString(lastActive, facility.dateFormat, facility.timeFormat, true)
                            }
                            else{
                                familyMember.lastActiveShortDisplay = '-'
                                familyMember.lastActiveFullDisplay = '-'
                            }
                            commit('setCurrentFamilyMember', familyMember);
                            dispatch('getResident', familyMember.residentId);
                            dispatch('getFamilyMemberVisits', familyMember);
                        }
                    });
            }
        },

        async getResidentWithUpcomingBirthdays({commit}) {
            try {
                const period = [
                    moment().add(-1, "days").locale('en-gb').format("MMM-DD"),
                    moment().locale('en-gb').format("MMM-DD"),
                    moment().add(1, "days").locale('en-gb').format("MMM-DD"),
                    moment().add(2, "days").locale('en-gb').format("MMM-DD"),
                    moment().add(3, "days").locale('en-gb').format("MMM-DD"),
                    moment().add(4, "days").locale('en-gb').format("MMM-DD"),
                    moment().add(5, "days").locale('en-gb').format("MMM-DD"),
                    moment().add(6, "days").locale('en-gb').format("MMM-DD"),
                    moment().add(7, "days").locale('en-gb').format("MMM-DD")
                ];

                const querySnapshot = await firebase
                    .firestore()
                    .collection("residents")
                    .where("facilityId", "==", this.state.authModule.currentFacility)
                    .where("status", "==", ResidentStatus.ActiveResident)
                    .where("shortDob", "in", period)
                    .orderBy("dateOfBirth")
                    .get();

                const today = moment().startOf('day');

                const res = querySnapshot.docs.map((doc) => {
                    const resident = doc.data();
                    const birthday = moment(resident.dateOfBirth.seconds * 1000).startOf('day');
                    const nextBirthday = birthday.clone().year(today.year());
                    const age = nextBirthday.diff(birthday, "years");
                    resident.dateOfBirth = moment(resident.dateOfBirth.seconds * 1000).toDate();
                    resident.age = age;
                    resident.id = doc.id;
                    return resident;
                });

                commit('setResidentsWithUpcomingBirthdays', res);
            } catch (error) {
                console.error("Unable to get residents upcoming birthdays", error);
            }
        },

        async getLeastEngagedResidents({commit}) {
            const fromDate = moment().add(-1, "months").toDate();
            const toDate = moment().toDate();
            const facilityId = this.state.authModule.currentFacility;
            const residents = this.state.residentModule.residentsShortData
            const types = [
                EventsTypes.Concierge,
                EventsTypes.ConciergeRequest,
                EventsTypes.FamilyVisit,
                EventsTypes.LifestylePrograms,
                EventsTypes.PremiumServices,
            ];

            const snap = await firebase
                .firestore()
                .collection("calender")
                .where("facilityId", "==", facilityId)
                .where("start", ">=", fromDate)
                .where("start", "<=", toDate)
                .where("type", "in", types)
                .get();

            let leastEngagedResidents = []

            const calenderEvents: any[] = serializer.querySnapshotToDocuments(snap).filter((x: any) => Object.prototype.hasOwnProperty.call(x, 'attendeesIds'));

            console.log(`leastEngagedResidents.events: ${calenderEvents.length}`)

            if (calenderEvents.length > 0) {
                // Create a map to store attendance count for each resident
                const attendanceCountMap = new Map();

                // Iterate through calendar events to count attendance for each resident
                for (const event of calenderEvents) {
                    const attendeesIds = event.attendeesIds || [];
                    for (const residentId of attendeesIds) {
                        attendanceCountMap.set(residentId, (attendanceCountMap.get(residentId) || 0) + 1);
                    }
                }

                // Iterate through residents to add residents without attendance
                residents.forEach((resident) => {
                    if (!attendanceCountMap.has(resident.id)) {
                        attendanceCountMap.set(resident.id, 0);
                    }
                });

                // Sort residents based on attendance count (least to most engaged)
                const residentsWithAttendance = Array.from(attendanceCountMap.entries()).map(([residentId, attendanceCount]) => ({
                    residentId,
                    attendanceCount,
                }));

                residentsWithAttendance.sort((a, b) => a.attendanceCount - b.attendanceCount);

                leastEngagedResidents = residentsWithAttendance.map((item) => {
                    const residentInfo = residents.find((resident) => resident.id === item.residentId) || {};
                    return {
                        residentId: item.residentId,
                        attendanceCount: item.attendanceCount,
                        name: residentInfo.name || "",
                        pname: residentInfo.pname || "",
                        thumbnailUrl: residentInfo.thumbnailUrl || DEFAULT_AVATAR_WHITE_200x200
                    };
                }).filter((x) => x.name.length > 0);
            }

            commit('setLeastEngagedResidents', leastEngagedResidents);
        },

        async updateFamilyMember({dispatch}, {id, data}) {
            await firebase
                .firestore()
                .collection('familyHubUsers')
                .doc(id)
                .update({...data})

            dispatch('getFamilyMember', {id: id});
            dispatch('getFamilyMembers');
            dispatch('updateFamilyMemberApprovalRequests', {id, data});
        },

        async updateFamilyMemberApprovalRequests(_, {id, data}) {
            const getApprovalRequests = firebase
                .firestore()
                .collection("additionalServiceRequestApprovals")
                .where("facilityId", "==", this.state.authModule.currentFacility)
                .where("familyHubMemberId", "==", id)

            const approvalRequests = await getApprovalRequests.get().then(querySnapshot => {
                return (querySnapshot.docs.map(x => ({
                    id: x.id,
                    ...x.data(),
                })) as any[])
            })

            for (let i = 0; i < approvalRequests.length; i++) {
                await firebase
                    .firestore()
                    .collection('additionalServiceRequestApprovals')
                    .doc(approvalRequests[i].id)
                    .update({
                        familyHubMemberName: data.name,
                    })
            }
        },

        async getFamilyMemberInvitationsByDates(
            {commit},
            {start = moment(), end = moment()}
        ) {
            const createdWithoutInvitationQuery = firebase
                .firestore()
                .collection("familyHubUsers")
                .where("facilityId", "==", this.state.authModule.currentFacility)
                .where("createdDateTime", ">=", moment(start).toDate())
                .where("createdDateTime", "<", moment(end).add(1, "day").toDate())

            const invitationsQuery = firebase
                .firestore()
                .collection("familyHubUsers")
                .where("facilityId", "==", this.state.authModule.currentFacility)
                .where("invitationSentDateTime", ">=", moment(start).toDate())
                .where("invitationSentDateTime", "<", moment(end).add(1, "day").toDate())

            const createdWithoutInvitationList = await createdWithoutInvitationQuery.get().then(querySnapshot => {
                return (querySnapshot.docs.map(x => ({
                    id: x.id,
                    ...x.data(),
                    invitationDateTime: moment(x.data().createdDateTime.seconds * 1000).toDate(),
                    status: MemberInviteStatuses.CreatedWithoutInvitation
                })) as any[])
                    .filter((x) => x.invitedToFamilyApp == null || x.invitedToFamilyApp == false);
            });

            const invitationsSentList = await invitationsQuery.get().then(querySnapshot => {
                return (querySnapshot.docs.map(x => ({
                    id: x.id,
                    ...x.data(),
                    invitationDateTime: moment(x.data().createdDateTime.seconds * 1000).toDate(),
                    status: MemberInviteStatuses.InvitationSent
                })) as any[])
                    .filter((x) => x.setPasswordToken && x.setPasswordToken.length > 0 && x.invitedToFamilyApp == true);
            });


            const invitationsAcceptedList = await invitationsQuery.get().then(querySnapshot => {
                return (querySnapshot.docs.map(x => ({
                    id: x.id,
                    ...x.data(),
                    invitationDateTime: moment(x.data().createdDateTime.seconds * 1000).toDate(),
                    status: MemberInviteStatuses.InvitationAccepted
                })) as any[])
                    .filter((x) => x.passwordSetDateTime != null);

            });

            const invitations = createdWithoutInvitationList.concat(invitationsAcceptedList).concat(invitationsSentList)

            commit("setFamilyMemberInvitations", invitations);
        },

        async getFamilyMemberVisits({commit}, familyMember) {
            const twoWeeksAgo = moment()
                .add(-14, 'days')
                .toDate();
            const tomorrow = moment()
                .add(1, 'days')
                .toDate();

            const visits = firebase
                .firestore()
                .collection('guestbookFamilyVisits')
                .where('facilityId', '==', this.state.authModule.currentFacility)
                .where('familyHubMemberId', '==', familyMember.id)
                .where('start', '>', twoWeeksAgo)
                .where('start', '<', tomorrow);

            await visits.get().then(querySnapshot => {
                const resList: Array<any> = [];
                querySnapshot.docs.forEach(x => {
                    const thisRes = x.data();
                    resList.push(thisRes);
                });
                commit('setCurrencyFamilyMemberVisits', resList);
            });

            //var chatActivity = function get chat count last fortnight.
        },

        async getResident({commit}, id) {
            if (!this.state?.currentResident || this.state.currentResident?.id !== id) {
                console.log(`getResident(${id})`)
                const familyMembers = await firebase
                    .firestore()
                    .collection('familyHubUsers')
                    .where('facilityId', '==', this.state.authModule.currentFacility)
                    .where('residentIds', 'array-contains', id)
                    .where('residentIds', '!=', null)
                    .get()
                    .then(qs => {
                        return qs.docs.map(doc => ({
                            id: doc.id,
                            ...doc.data(),
                        }));
                    });

                const resident = firebase
                    .firestore()
                    .collection('residents')
                    .doc(id);

                return await resident.get().then(querySnapshot => {
                    const currentRes = querySnapshot.data();
                    if (currentRes != null) {
                        currentRes.id = querySnapshot.id;
                        currentRes.familyHubUsers = familyMembers || [];
                        currentRes.familyHubUsersIds = currentRes?.familyHubUsersIds?.filter(x=>x?.length > 0) || []

                        commit('setCurrentResident', currentRes);
                        return currentRes
                    }
                    return null
                });
            }
        },

        async getUpcomingCallsByResident({ commit }, { residentId } ) {
            const fromDate = moment().toDate();
            const toDate = moment().add(1, "months").toDate();
            const types = [EventsTypes.ScheduledCall, EventsTypes.RealtimeCall, EventsTypesMinor.ScheduledCall];
            const baseWhere = [
                { field: 'start', operator: '>=', value: fromDate },
                { field: 'start', operator: '<=', value: toDate }
            ];

            const fetchEvents = async (additionalCondition) => {
                const whereConditions = [...baseWhere, additionalCondition];
                const events = await getWhereIdInIdsExtended('type', types, 'calender', this.state.authModule.currentFacility, whereConditions);
                return events.map(x => ({
                    start: moment(x.start.seconds * 1000).toDate(),
                    end: moment(x.end.seconds * 1000).toDate(),
                    participants: [],
                    ...x
                }));
            };

            const [calenderEvents1, calenderEvents2] = await Promise.all([
                fetchEvents({ field: 'familyCallInvitees', operator: 'array-contains', value: residentId }),
                fetchEvents({ field: 'going', operator: 'array-contains', value: residentId })
            ]);

            const result = [...calenderEvents1, ...calenderEvents2].filter((obj, index, self) =>
              index === self.findIndex((o) => o.id === obj.id)
            );

            commit("setUpcomingCallsByResident", result);
        },

        async residentDeparture({dispatch}, residentId: any) {
            try {
                await firebase
                    .firestore()
                    .collection('residents')
                    .doc(residentId).get().then(async (residentDocRef) => {

                        await firebase
                            .firestore()
                            .collection("additionalServices")
                            .where("facilityId", "==", this.state.authModule.currentFacility)
                            .where("membersIds", "array-contains", residentDocRef.id)
                            .get()
                            .then((querySnapshot) => {
                                querySnapshot.docs.forEach(service => {
                                    const serviceData = service.data();
                                    const membersList = serviceData.membersList.filter(x => x.id !== residentDocRef.id);
                                    const membersIds = serviceData.membersIds.filter(x => x !== residentDocRef.id);

                                    service.ref.update({
                                        "membersList": membersList,
                                        "membersIds": membersIds
                                    });
                                })
                            });

                        const residentData = residentDocRef.data();

                        await firebase
                            .firestore()
                            .collection('devices')
                            .where('facilityId', '==', this.state.authModule.currentFacility)
                            .where('userId', '==', residentDocRef.id)
                            .get()
                            .then(async (deviceSnapshot) => {
                                if (deviceSnapshot.docs && deviceSnapshot.docs.length) {
                                    for (let i = 0; i < deviceSnapshot.docs.length; i++) {
                                        const device = deviceSnapshot.docs[i];
                                        const response = await firebase.app().functions(getRegionName()).httpsCallable("resident-sendUnassignNotification")({deviceId: device.id})

                                        if (response.data.status === 200) {
                                            await device.ref.delete();
                                        } else {
                                            return false
                                        }
                                    }

                                    return true
                                }
                            }).then(async () => {
                                await dispatch("getDevices");
                                await dispatch('getResident', residentId);
                                await dispatch("getDevicesByResidentId", residentId);
                                return true;
                            })

                        await firebase
                            .firestore()
                            .collection('residents')
                            .doc(residentDocRef.id)
                            .update({
                                status: ResidentStatus.Departed,
                                departedTime: new Date(),
                            })
                            .then(async () => {
                                console.log(`resident: ${residentDocRef.id}`)

                                const familyMembersQuery = await firebase
                                    .firestore()
                                    .collection('familyHubUsers')
                                    .where('facilityId', '==', this.state.authModule.currentFacility)
                                    .where('residentIds', 'array-contains', residentId).get()

                                const familyMembers = serializer.querySnapshotToDocuments<any>(familyMembersQuery)

                                for (const familyMember of familyMembers) {
                                    {
                                        const familyMemberResidentsQuery = await firebase
                                            .firestore()
                                            .collection('residents')
                                            .where('facilityId', '==', this.state.authModule.currentFacility)
                                            .where('familyHubUsersIds', 'array-contains', familyMember.id).get()

                                        const familyHubUserDocRef = firebase.firestore().collection('familyHubUsers').doc(familyMember.id);
                                        const residentDocRef = firebase.firestore().collection('residents').doc(residentId);

                                        // set 'Archive' status for familyHubUser.resident item
                                        let residentToUpdate = familyMember.residents.find(x => x.id == residentDocRef.id) || []
                                        console.log('residentToUpdate: ', residentToUpdate)
                                        if (!residentToUpdate) {
                                            residentToUpdate = {
                                                id: residentDocRef.id,
                                                name: residentData.name,
                                                status: ResidentStatus.Departed,
                                            }
                                        }
                                        residentToUpdate.status = ResidentStatus.Departed

                                        const familyHubUserResidents = familyMember.residents.filter(x => x.id != residentDocRef.id) || []
                                        familyHubUserResidents.push(residentToUpdate)

                                        familyHubUserDocRef.update({
                                            residents: familyHubUserResidents
                                        })


                                        const familyMemberResidents = serializer.querySnapshotToDocuments<any>(familyMemberResidentsQuery) || []

                                        console.log(`familyMemberResidents: ${familyMemberResidents.length}`)

                                        if (familyMemberResidents.length > 0 && familyMemberResidents.every(x => x.status === ResidentStatus.Departed)) {

                                            //set 'Archive' status for familyHubUser doc and familyHubUser item in resident.familyHubUsers collection

                                            await firebase
                                                .firestore()
                                                .runTransaction(async (transaction) => {

                                                    const archivedDateTime = new Date()

                                                    // update familyHubUser doc
                                                    const familyHubUserDocRef = firebase.firestore().collection('familyHubUsers').doc(familyMember.id);

                                                    transaction.update(familyHubUserDocRef, {
                                                        status: FamilyHubUserStatus.Archived,
                                                        archivedDateTime: archivedDateTime,
                                                        appStatus: FamilyHubAppStatus.ReadOnly
                                                    })

                                                    // update resident.familyHubUsers item

                                                    let familyMemberToUpdate = residentData.familyHubUsers.find(x => x.id == familyMember.id)

                                                    if (!familyMemberToUpdate) {
                                                        familyMemberToUpdate = familyMember
                                                    }

                                                    familyMemberToUpdate.status = FamilyHubUserStatus.Archived
                                                    familyMemberToUpdate.archivedDateTime = new Date();
                                                    familyMemberToUpdate.appStatus = FamilyHubAppStatus.ReadOnly;

                                                    const residentFamilyMembers = residentData.familyHubUsers.filter(x => x.id != familyMember.id) || []

                                                    residentFamilyMembers.push(familyMemberToUpdate)

                                                    transaction.update(residentDocRef, {
                                                        familyHubUsers: residentFamilyMembers
                                                    })

                                                    console.log(`family member archived: ${familyMember.id}`)
                                                })
                                                .catch(e => {
                                                    console.error(`Error. Family member not archived: ${familyMember.id}`, e)
                                                });

                                        } else {
                                            console.log(`family member not archived: ${familyMember.id}`)
                                        }

                                    }
                                }
                            });
                    })
            } catch (error) {
                console.log(error)
            }
        },
        async undepartResident({dispatch}, {residentId, isUnarchiveMembers}) {
            console.log('undepart resident: ' + residentId)
            const residentData: any = serializer.documentSnapshotToDocument(await firebase.firestore().collection('residents').doc(residentId).get())

            await firebase
                .firestore()
                .collection('residents')
                .doc(residentId)
                .update({
                    status: ResidentStatus.ActiveResident,
                    prevDepartedTime: residentData.departedTime || null,
                    departedTime: null,
                })

            if (isUnarchiveMembers) {
                console.log('unarchiving members')
                const familyMembers: any[] = serializer.querySnapshotToDocuments<any>(
                    await firebase.firestore().collection('familyHubUsers')
                        .where('facilityId', '==', this.state.authModule.currentFacility)
                        .where('residentIds', 'array-contains', residentId)
                        .get())

                for (const familyMember of familyMembers) {
                    const familyHubUserDocRef = firebase.firestore().collection('familyHubUsers').doc(familyMember.id);
                    const residentDocRef = firebase.firestore().collection('residents').doc(residentId);

                    // set 'Active Resident' status for familyHubUser.resident item
                    let residentToUpdate = familyMember.residents.find(x => x.id == residentDocRef.id) || []
                    if (!residentToUpdate) {
                        residentToUpdate = {
                            id: residentDocRef.id,
                            name: residentData.name,
                            status: ResidentStatus.ActiveResident,
                        }
                    }
                    residentToUpdate.status = ResidentStatus.ActiveResident

                    const familyHubUserResidents = familyMember.residents.filter(x => x.id != residentDocRef.id) || []
                    familyHubUserResidents.push(residentToUpdate)
                    familyHubUserDocRef.update({residents: familyHubUserResidents})

                    //set 'Active' status for familyHubUser doc and familyHubUser item in resident.familyHubUsers collection
                    if (familyMember.status === FamilyHubUserStatus.Archived) {
                        await firebase
                            .firestore()
                            .runTransaction(async (transaction) => {

                                // update familyHubUser doc
                                const familyHubUserDocRef = firebase.firestore().collection('familyHubUsers').doc(familyMember.id);

                                transaction.update(familyHubUserDocRef, {
                                    status: FamilyHubUserStatus.Active,
                                    archivedDateTime: null,
                                    prevArchivedDateTime: familyMember.archivedDateTime,
                                    appStatus: familyMember.appStatus == FamilyHubAppStatus.ReadOnly ? FamilyHubAppStatus.Activated : familyMember.appStatus
                                })

                                // update resident.familyHubUsers item
                                let familyMemberToUpdate = residentData.familyHubUsers.find(x => x.id == familyMember.id)

                                if (!familyMemberToUpdate) {
                                    familyMemberToUpdate = familyMember
                                }

                                familyMemberToUpdate.status = FamilyHubUserStatus.Active
                                familyMemberToUpdate.archivedDateTime = new Date();
                                familyMemberToUpdate.appStatus = familyMemberToUpdate.appStatus == FamilyHubAppStatus.ReadOnly ? FamilyHubAppStatus.Activated : familyMemberToUpdate.appStatus;
                                const residentFamilyMembers = residentData.familyHubUsers.filter(x => x.id != familyMember.id) || []
                                residentFamilyMembers.push(familyMemberToUpdate)
                                transaction.update(residentDocRef, {familyHubUsers: residentFamilyMembers})
                            })
                            .catch(e => {
                                console.error(`Error. Family member not activated: ${familyMember.id}`, e)
                            });
                    }
                }
            }

            await dispatch('getResident', residentId);
        },
        async residentTransfer({dispatch}, data) {
            return await firebase.app().functions(getRegionName())
                .httpsCallable('transferResident')({
                    residentId: data.residentId,
                    newFacilityId: data.facilityId,
                })
                .then(snap => {
                    return snap.data;
                });
        },

        async deleteResident(_, residentId) {

            if (!residentId) return

            Sentry.captureMessage(`Deleting resident ${residentId}`);

            let facilityId = null;
            let residentFamilyMembersDocs = null;
            let residentFamilyMembers = null;
            let familyMembersIds = [];
            let residentsOfFamilyMembers = [];

            try {
                facilityId = this.state.authModule.currentFacility;
                residentFamilyMembersDocs = await firebase.firestore().collection('familyHubUsers').where('facilityId', '==', facilityId).where('residentIds', 'array-contains', residentId).get()
                residentFamilyMembers = serializer.querySnapshotToDocuments<any>(residentFamilyMembersDocs)
                familyMembersIds = residentFamilyMembers.map(x => x.id)

                if (familyMembersIds.length) {
                    const residentsOfFamilyMembersDocs = await firebase.firestore().collection('residents').where('facilityId', '==', facilityId).where('familyHubUsersIds', 'array-contains-any', familyMembersIds).get()
                    residentsOfFamilyMembers = serializer.querySnapshotToDocuments<any>(residentsOfFamilyMembersDocs)
                }
            }
            catch (error){
                Sentry.captureException(error);
            }
            finally {
                Sentry.captureMessage(`familyMembersIds: ${familyMembersIds.join(', ')}`);
                Sentry.captureMessage(`residentsOfFamilyMembers: ${residentsOfFamilyMembers.join(', ')}`)
            }

            await firebase
                .firestore()
                .runTransaction(async (transaction) => {

                    const residentDocRef = firebase.firestore().collection('residents').doc(residentId)
                    const residentDoc = await transaction.get(residentDocRef)

                    if (residentDoc.exists) {

                        const residentData = residentDoc.data()


                        if (residentData?.familyHubUsersIds?.length) {

                            for (const famId of residentData.familyHubUsersIds) {
                                const familyMemberDoc = residentFamilyMembersDocs.docs.find(x => x.id === famId)
                                if (familyMemberDoc) {
                                    const hasOtherActiveResident = residentsOfFamilyMembers.some(x => x.id !== residentId && x.familyHubUsersIds.includes(famId) && x.status === ResidentStatus.ActiveResident)
                                    const familyMemberData = residentFamilyMembers.find(x => x.id === famId)
                                    const familyMemberResidents = familyMemberData?.residents ? familyMemberData.residents.filter(x => x.id !== residentId) : [];
                                    const familyMemberResidentsIds = familyMemberData?.residentIds ? familyMemberData.residentIds.filter(x => x !== residentId) : [];
                                    const familyMemberFinancialEPOA = familyMemberData?.financialEPOA ? familyMemberData.financialEPOA.filter(x => x.id !== residentId) : [];
                                    const residentIdObsoleteField = familyMemberData.residentId == residentId ? null : familyMemberData.residentId
                                    const residentNameObsoleteField = residentIdObsoleteField == null ? '' : familyMemberData.residentName

                                    if (hasOtherActiveResident) {
                                        transaction.update(familyMemberDoc.ref, {
                                            residentName: residentNameObsoleteField,
                                            residentId: residentIdObsoleteField,
                                            residents: familyMemberResidents || null,
                                            residentIds: familyMemberResidentsIds || null,
                                            financialEPOA: familyMemberFinancialEPOA || null
                                        })
                                    } else {
                                        transaction.update(familyMemberDoc.ref, {
                                            residentName: residentNameObsoleteField,
                                            residentId: residentIdObsoleteField,
                                            residents: familyMemberResidents || null,
                                            residentIds: familyMemberResidentsIds || null,
                                            financialEPOA: familyMemberFinancialEPOA || null,
                                            appStatus: FamilyHubAppStatus.ReadOnly,
                                            status: FamilyHubUserStatus.Archived,
                                            statusReason: FamilyHubUserStatusReason.ResidentDeleted
                                        })
                                    }
                                }
                            }
                        }

                        transaction.delete(residentDocRef)

                        const devices = await firebase.firestore().collection('devices').where('facilityId', '==', facilityId).where('userId', '==', residentDoc.id).get()

                        if (devices.size > 0) {
                            devices.docs.forEach(deviceRef => {
                                transaction.delete(deviceRef.ref)
                            });
                        }
                    }

                }).catch(e => {
                    console.error(e);
                    Sentry.captureException(e);
                });
        },

        async unAssignDevice({dispatch}, {userId, deviceId, userType}) {
            console.log(`unAssignDevice(${userId} , ${deviceId})`)

            const facilityId = this.state.authModule.currentFacility;

            const deviceSnap = await firebase.firestore().collection('devices').doc(deviceId).get()

            const device = deviceSnap.exists ? deviceSnap.data() : null

            if (!deviceSnap.exists || device.facilityId != facilityId) {
                throw new Error('Device not found')
            }

            if (deviceSnap.data().userId != userId) {
                throw new Error('Device not assigned to this user')
            }

            const response = await firebase.app().functions(getRegionName()).httpsCallable("resident-sendUnassignNotification")({deviceId: deviceId})

            if (response.data.status === 200 || response.data.status === 400) {
                return await deviceSnap.ref
                    .delete().then(async () => {
                        await dispatch("getDevices");

                        if (userType === UserTypeEnum.Resident) {
                            await dispatch('getResident', userId);
                            await dispatch("getDevicesByResidentId", userId);
                        }
                        return true
                    }).catch((e) => {
                        console.error(e)
                        return false
                    })
            } else {
                return false
            }
        },

        async deleteFamilyHubUserFromResident({state, dispatch}, payload) {
            const facilytyId = this.state.authModule.currentFacility;

            if (!payload || !payload.familyHubUser) {
                console.error('No familyHubUser provided');
                return;
            }

            const familyHubUser = await firebase
                .firestore()
                .collection('familyHubUsers')
                .where('facilityId', '==', facilytyId)
                .where('email', '==', payload.familyHubUser.email)
                .get()
                .then(qs => {
                    if (!qs.empty) {
                        const doc = qs.docs[0];
                        return {id: doc.id, ...doc.data()} as FamilyHubUser;
                    }

                    return null;
                })
                .catch(e => console.error(e));


            const residentSnap = await firebase.firestore().collection('residents').doc(payload.residentId).get()
            const residentData = residentSnap.data()

            if (!familyHubUser) {
                const familyHubUsers = residentData.familyHubUsers.filter(x => x.id !== payload.familyHubUser.id)

                return residentSnap.ref.update({
                    familyHubUsers: familyHubUsers,
                    familyHubUsersIds: firebase.firestore.FieldValue.arrayRemove(payload.familyHubUser.id)
                })
                    .then(() => {
                        return dispatch('getResident', state.currentResident.id)
                    });
            }

            await dispatch('deleteFamilyMemberFromServiceRequestEPOAList', payload.familyHubUser.id);

            return await firebase
                .firestore()
                .runTransaction(transaction => {
                    const familyHubUserDocRef = firebase
                        .firestore()
                        .collection('familyHubUsers')
                        .doc(familyHubUser.id);

                    return transaction
                        .get(familyHubUserDocRef)
                        .then(async familyHubUserDoc => {
                            if (!familyHubUserDoc.exists) {
                                throw 'FamilyHubUser not exist!';
                            }

                            const familyUserData: any = {id: familyHubUserDoc.id, ...familyHubUserDoc.data()};

                            const familyUserResidents = familyUserData.residents ? familyUserData.residents.filter(
                                x => x.id !== payload.residentId
                            ) : []

                            const financialEPOA = familyUserData.financialEPOA ? familyUserData.financialEPOA.filter(
                                x => x.id !== payload.residentId
                            ) : []

                            const welfarePOA = familyUserData.welfarePOA ? familyUserData.welfarePOA.filter(
                                x => x.id !== payload.residentId
                            ) : []

                            const enduringPOA = familyUserData.enduringPOA ? familyUserData.enduringPOA.filter(
                                x => x.id !== payload.residentId
                            ) : []

                            transaction.update(familyHubUserDocRef, {
                                residentIds: firebase.firestore.FieldValue.arrayRemove(payload.residentId),
                                residents: familyUserResidents,
                                financialEPOA: financialEPOA,
                                welfarePOA: welfarePOA,
                                enduringPOA: enduringPOA,
                            });


                            if (!familyUserResidents.length) {
                                transaction.delete(familyHubUserDocRef).update(residentSnap.ref, {
                                    familyHubUsers: residentData.familyHubUsers.filter(x => x.email !== familyUserData.email),
                                    familyHubUsersIds: residentData.familyHubUsers.filter(x => x.email !== familyUserData.email).filter(f => f.id).map(x => x.id) || null,
                                });
                            } else {
                                transaction.update(residentSnap.ref, {
                                    familyHubUsers: residentData.familyHubUsers.filter(x => x.email !== familyUserData.email),
                                    familyHubUsersIds: residentData.familyHubUsers.filter(x => x.email !== familyUserData.email).filter(f => f.id).map(x => x.id) || null,
                                });
                            }
                        });
                })
                .then(() => dispatch('getResident', state.currentResident.id))
                .catch(e => console.error(e));
        },

        async deleteFamilyHubUserFromProfile({state, dispatch}, id) {
            if (!id) return

            await firebase.firestore().collection('familyHubUsers').doc(id).delete()
        },

        async deleteFamilyMemberFromServiceRequestEPOAList(_, familyMemberId) {
            await firebase
                .firestore()
                .collection("additionalServiceRequestApprovals")
                .where("facilityId", "==", this.state.authModule.currentFacility)
                .where("familyHubMemberId", "==", familyMemberId).get().then((querySnapshot: any) => {
                    if (querySnapshot.docs.length) {
                        querySnapshot.docs.forEach(async doc => {
                            await doc.ref.delete()
                        });
                    }
                })
        },

        async addFamilyHubUser({state, dispatch}, data) {

            if (!data || !data.familyHubUser || !data.residentId) throw new Error('Invalid input parameters')

            const newFamilyHubUser = data.familyHubUser
            newFamilyHubUser.facilityId = this.state.authModule.currentFacility
            newFamilyHubUser.residentId = data.residentId

            const response = await firebase.app().functions(getRegionName()).httpsCallable("familyUser-create")(newFamilyHubUser)

            if (response.data.status === 200) {
                return await dispatch('getResident', newFamilyHubUser.residentId);
            } else {
                throw new Error(`Unable to create family member`)
            }
        },

        async importFamilyHubUser({state}, newFamilyHubUser) {

            if (newFamilyHubUser.invitedToFamilyApp) {
                newFamilyHubUser.appStatus = FamilyHubAppStatus.Pending;
            } else {
                newFamilyHubUser.appStatus = FamilyHubAppStatus.NotAdded;
            }

            const docs = (
                await firebase
                    .firestore()
                    .collection('familyHubUsers')
                    .where('email', '==', newFamilyHubUser.email)
                    .where('facilityId', '==', newFamilyHubUser.facilityId)
                    .get()
            ).docs;

            newFamilyHubUser.facilityName = this.state.authModule.currentFacilityData.name;
            newFamilyHubUser.imageUrl = DEFAULT_AVATAR_WHITE
            newFamilyHubUser.thumbnailUrl = DEFAULT_AVATAR_WHITE_200x200
            newFamilyHubUser.createdDateTime = firebase.firestore.Timestamp.fromDate(new Date())
            newFamilyHubUser.status = newFamilyHubUser.status || FamilyHubUserStatus.Active

            if (docs.length <= 0) {

                console.log('add member')

                newFamilyHubUser.residents[0].id = newFamilyHubUser.residentId;
                newFamilyHubUser.residents[0].name = newFamilyHubUser.residentName;

                return await firebase
                    .firestore()
                    .collection('familyHubUsers')
                    .add(newFamilyHubUser)
                    .then(async (result) => {

                        //add user to resident
                        newFamilyHubUser.id = result.id;
                        return await firebase
                            .firestore()
                            .collection('residents')
                            .doc(newFamilyHubUser.residentId)
                            .update({
                                familyHubUsers: firebase.firestore.FieldValue.arrayUnion(newFamilyHubUser),
                                familyHubUsersIds: firebase.firestore.FieldValue.arrayUnion(result.id)
                            }).then(() => {
                                return true
                            })
                    });
            } else if (docs.length == 1) {

                console.log('update member')
                const resident = await (await firebase.firestore().collection("residents").doc(newFamilyHubUser.residentId).get()).data()

                //updated existing user
                if (
                    (resident.familyHubUsers && resident.familyHubUsers?.length && !resident.familyHubUsers.some(x => x.email == newFamilyHubUser.email)) ||
                    !resident.familyHubUsers && !resident.familyHubUsers?.length
                ) {
                    return await firebase
                        .firestore()
                        .collection('familyHubUsers')
                        .doc(docs[0].id)
                        .update({
                            residentIds:
                                firebase.firestore.FieldValue.arrayUnion(
                                    newFamilyHubUser.residentId
                                ) || [],
                            residents:
                                firebase.firestore.FieldValue.arrayUnion({
                                    id: newFamilyHubUser.residentId,
                                    name: newFamilyHubUser.residentName,
                                    pname: newFamilyHubUser?.pname || resident?.pname || '',
                                }) || [],
                            financialEPOA:
                                firebase.firestore.FieldValue.arrayUnion({
                                    id: newFamilyHubUser.residentId,
                                    status: newFamilyHubUser.financialEPOA.find(x=>x.id == newFamilyHubUser.residentId)?.status || false,
                                }) || [],

                            welfarePOA:
                                firebase.firestore.FieldValue.arrayUnion({
                                    id: newFamilyHubUser.residentId,
                                    status: newFamilyHubUser.welfarePOA.find(x=>x.id == newFamilyHubUser.residentId)?.status || false,
                                }) || [],

                                enduringPOA:
                                firebase.firestore.FieldValue.arrayUnion({
                                    id: newFamilyHubUser.residentId,
                                    status: newFamilyHubUser.enduringPOA.find(x=>x.id == newFamilyHubUser.residentId)?.status || false,
                                }) || [],
                            })
                        .then(async () => {
                            //add user to resident
                            newFamilyHubUser.id = docs[0].id;
                            return await firebase
                                .firestore()
                                .collection('residents')
                                .doc(newFamilyHubUser.residentId)
                                .update({
                                    familyHubUsers: firebase.firestore.FieldValue.arrayUnion(newFamilyHubUser),
                                    familyHubUsersIds: firebase.firestore.FieldValue.arrayUnion(docs[0].id)
                                }).then(() => {
                                    return true
                                })
                        });
                } else {
                    console.log('User already exists in resident');
                    return Promise.resolve(false)
                }
            } else if (docs.length > 1) {
                console.error('More than one user with the same email detected');
                return Promise.reject()
            }
            return Promise.reject()
        },

        updateStoryAndPreferences({state}, {myStory, preferences}) {
            firebase
                .firestore()
                .collection('residents')
                .doc(state.currentResident.id)
                .update({
                    aboutMe: myStory || '',
                    preferences: preferences || '',
                });
        },

        async admitResident({dispatch}, resident) {
            resident.facilityId = this.state.authModule.currentFacility;
            resident.facilityName = this.state.authModule.currentFacilityData.name;
            resident.createdDateTime = firebase.firestore.Timestamp.fromDate(new Date())
            resident.refOrigin = 'ResHub'

            if (resident.email) {
                resident.email = resident.email.trim().toLowerCase()
            }

            const snap = await firebase.firestore().collection('residents').add(resident);
            await firebase.firestore().collection('residents').doc(snap.id).update({ refId: snap.id });
            return snap;
        },

        async updateResidentInfo({dispatch, state}, resident) {
            console.log("updateResidentInfo", resident)

            const residentId = resident.id

            const prevData = state.residentsShortData.find(x => x.id === residentId);

            if(resident?.hasOwnProperty('id')){
                delete resident.id
            }

            if (resident.email) {
                resident.email = resident.email.trim().toLowerCase()

                if (prevData.email !== resident.email) {
                    resident.isEmailVerified = false
                }
            }

            return await firebase
                .firestore()
                .collection('residents')
                .doc(residentId)
                .update(resident)
                .then(async () => {
                    await dispatch('getFamilyMembers');
                    await dispatch("getResident", residentId)
                })

            //residents data which contains in the other documents (like familyHubUsers.residents) updating in the trigger.
        },

        async updateResidentTags({dispatch}, data) {
            try {
                const residentSnap = await firebase.firestore().collection('residents').doc(data.residentId).get();

                if (!residentSnap.exists) {
                    throw new Error('Resident not found');
                }

                const tagTypes = ['interestsTags', 'preferencesTags', 'backgroundTags'];

                for (const tagType of tagTypes) {
                    const tagsToAdd = data[tagType].filter(tag => !tag.id && tag.name?.length > 0).map(tag => tag.name);
                    const {addedTagIds} = tagsToAdd.length > 0 ? await dispatch('addTags', {
                        tagsNames: tagsToAdd,
                        entity: UserTypeEnum.Resident
                    }) : [];
                    const existingTagIds = data[tagType].filter(tag => tag.id?.length > 0).map(tag => tag.id);
                    const updatedTags = (addedTagIds || []).concat(existingTagIds);
                    await residentSnap.ref.update({[tagType]: updatedTags});
                }
            } catch (error) {
                console.error('Error updating resident tags:', error);
            }
        },

        async sendNotificationToFamilyMember(_, payload) {
          return await firebase.app().functions(getRegionName())
                .httpsCallable('groupD-sendNotificationToFamilyMember')({
                    ...payload,
                    facilityId: this.state.authModule.currentFacility,
                    familyMemberId: payload.familyMember.id,
                });
        },

        async checkIfResidentEmailExists(_, data) {
            const emailAddress = data.emailAddress.trim().toLowerCase()

            const result = await firebase.app().functions(getRegionName())
                .httpsCallable("checkIfResidentEmailExists")({
                    emailAddress: emailAddress,
                    skipResidentId: data.skipResidentId || null
                })

            if (result.data.status === 200) {
                return result.data.data;
            }

            return true
        },

        async sendArchiveProfile(_, data) {
            console.log("sendArchiveProfile")
            await firebase
                .firestore()
                .collection('email')
                .add({
                    to: data.email,
                    facilityId: this.state.authModule.currentFacility,
                    message: {
                        subject: "Staffhub Person Data",
                        html: `<b>Name:</b> ${data.name}`,
                        attachments: data.attachments
                    }
                }).then(() => {
                    console.log("Success Send")
                })
        },

        async subscribeOnResidentsCollection({commit, state}) {
            const query = firebase
                .firestore()
                .collection("residents")
                .where('facilityId', '==', this.state.authModule.currentFacility)

            state.onResidentsUnsubscribe = await query
                .onSnapshot((snapshot) => {
                    state.isOneSubscribeOnResidents = false;
                    snapshot.docChanges().forEach(async (change) => {

                        if (change.type === "added" && change.doc.data().createdDateTime >= firebase.firestore.Timestamp.fromMillis(Date.now())) {
                            console.log(change)
                            const resident = {id: change.doc.id, ...change.doc.data()}
                            commit("setResidentsShortData", [resident, ...state.residentsShortData]);
                        }

                        if (change.type === "modified") {
                            console.log(`resident ${change.doc.id} modified`);
                          
                            const changeDocData = change.doc.data();
                            const existing = state.residentsShortData.find(x => x.id === change.doc.id);
                          
                            if (existing) {
                              ResidentParams.forEach(param => {
                                if (param in changeDocData) {
                                  if (changeDocData[param]?.seconds) {
                                    existing[param] = moment(changeDocData[param].seconds * 1000).toDate();
                                  } else {
                                    existing[param] = changeDocData[param];
                                  }
                                }
                              });

                              const residents = state.residentsShortData.filter(x => x.id !== change.doc.id);
                              commit("setResidentsShortData", [existing, ...residents]);
                            }
                        }
                          

                        if (change.type === "removed") {
                            console.log(change)
                            const residents = state.residentsShortData.filter(x => x.id !== change.doc.id);
                            commit("setResidentsShortData", [...residents]);
                        }
                    })
                })
        },

        async unsubscribeOnResidentsCollection({state}) {
            if (state.onResidentsUnsubscribe) {
                state.onResidentsUnsubscribe();
            }
        }
    },
};
export default residentModule;
